import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Row from '../../../core/layout/Row';
import Column from '../../../core/layout/Column';
import TextInput from '../../../core/form/TextInput';
import FormButtons from '../../../core/form/FormButtons';
import MaskInput from '../../../core/form/MaskInput';
import FetchSelect from '../../../core/form/FetchSelect';
import PermissionValidator from '../../../core/layout/PermissionValidator';
import Switch from '../../../core/form/Switch';
import { Roles } from '../../../core/util';
import { BRAZIL, DOCUMENT_NUMBER_MASK_OPTIONS } from '../../../core/consts';

const CompanyForm = (props) => {
  const maskInputRef = useRef();

  const handleDocumentNumberChange = useCallback(
    (value) => {
      if (value.length > 11) {
        maskInputRef.current.updateMaskOptions(DOCUMENT_NUMBER_MASK_OPTIONS.cnpj);
      } else {
        maskInputRef.current.updateMaskOptions(DOCUMENT_NUMBER_MASK_OPTIONS.cpf);
      }
      props.onChange('cnpj', value);
    },
    [props]
  );

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        props.onSubmit();
      }}
    >
      <Row>
        <Column size={3}>
          <FetchSelect
            required
            showSearch
            url="/countries"
            disabled={props.submitting || (get(props.values, 'id', null) && get(props.values, 'contract', null))}
            label="Country"
            value={get(props.values, 'country', '')}
            onChange={(country) => props.onChange('country', country)}
            errorText={get(props.errors, 'country', '')}
            resultTransformer={{
              text: 'name',
              value: 'id',
            }}
          />
        </Column>
        <Column size={3}>
          <FetchSelect
            required
            showSearch
            url="/states"
            params={{
              ...(get(props.values, 'country', false) && {
                country: get(props.values, 'country', ''),
              }),
            }}
            disabled={
              props.submitting ||
              !get(props.values, 'country', false) ||
              (get(props.values, 'id', null) && get(props.values, 'contract', null))
            }
            label="State"
            value={get(props.values, 'state', '')}
            onChange={(state) => props.onChange('state', state)}
            errorText={get(props.errors, 'state', '')}
            resultTransformer={{
              text: 'name',
              value: 'id',
            }}
          />
        </Column>
        <Column size={6}>
          <TextInput
            floatingLabel
            fullWidth
            disabled={props.submitting || (get(props.values, 'id', null) && get(props.values, 'contract', null))}
            label="City"
            value={get(props.values, 'city', '')}
            onChange={(value) => props.onChange('city', value)}
            errorText={get(props.errors, 'city', '')}
          />
        </Column>
      </Row>
      <Row>
        <Column size={localStorage.role === Roles.ADMIN ? 4 : 9}>
          <TextInput
            disabled={props.submitting}
            label="Company Name"
            value={get(props.values, 'name', '')}
            onChange={(value) => props.onChange('name', value)}
            errorText={get(props.errors, 'name', null)}
            required
          />
        </Column>
        <Column size={3}>
          {BRAZIL.includes(props.values.country) ? ( // Brazil
            <MaskInput
              disabled={props.submitting || (get(props.values, 'id', null) && get(props.values, 'contract', null))}
              label="CPF/CNPJ"
              value={get(props.values, 'cnpj', '')}
              onChange={handleDocumentNumberChange}
              errorText={get(props.errors, 'cnpj', '')}
              delimiters={['.', '.', '/', '-']}
              blocks={[2, 3, 3, 4, 2]}
              numericOnly
              required
              ref={maskInputRef}
            />
          ) : (
            <TextInput
              required
              disabled={props.submitting || (get(props.values, 'id', null) && get(props.values, 'contract', null))}
              label="Company Document Number"
              value={get(props.values, 'cnpj', '')}
              onChange={(value) => props.onChange('cnpj', value)}
              errorText={get(props.errors, 'cnpj', '')}
            />
          )}
        </Column>
        <PermissionValidator allowedFor={[Roles.ADMIN]}>
          <Column size={get(props.values, 'distributor.name', '').toLowerCase() === 'flexge' ? 3 : 5}>
            <FetchSelect
              showSearch
              url="/distributors"
              required
              disabled={
                props.submitting ||
                props.disableDistributor ||
                (get(props.values, 'id', null) && get(props.values, 'contract', null))
              }
              label="Distributor"
              value={get(props.values, 'distributor.id', '')}
              onChange={(id, distributor) => props.onChange('distributor', distributor)}
              errorText={get(props.errors, 'distributor', null)}
              resultTransformer={{
                text: 'name',
                value: 'id',
              }}
            />
          </Column>
          {get(props.values, 'distributor.name', '').toLowerCase() === 'flexge' && (
            <Column size={2}>
              <FetchSelect
                showSearch
                required
                url="/segments"
                disabled={props.submitting}
                label="Segment"
                value={get(props.values, 'segment', '')}
                onChange={(segment) => props.onChange('segment', segment)}
                errorText={get(props.errors, 'segment', '')}
                resultTransformer={{
                  text: 'name',
                  value: 'id',
                }}
              />
            </Column>
          )}
        </PermissionValidator>
      </Row>
      <Row>
        <Column size={2}>
          <Switch
            label="Is Certification Test disabled?"
            titleOff="No"
            titleOn="Yes"
            onChange={(value) => props.onChange('isCertificationTestDisabled', value)}
            value={get(props.values, 'isCertificationTestDisabled', false)}
            disabled={props.submitting}
          />
        </Column>
        <Column size={1.5}>
          <TextInput
            type="number"
            disabled={props.submitting}
            label="Demo Limit"
            value={get(props.values, 'demoStudentLimit', '')}
            onChange={(value) => props.onChange('demoStudentLimit', value)}
            errorText={get(props.errors, 'demoStudentLimit', null)}
          />
        </Column>
        <Column size={1.5}>
          <TextInput
            type="number"
            disabled={props.submitting}
            label="Login Limit"
            value={get(props.values, 'studentLoginLimit', '')}
            onChange={(value) => props.onChange('studentLoginLimit', value)}
            errorText={get(props.errors, 'studentLoginLimit', null)}
          />
        </Column>
      </Row>
      <FormButtons
        confirmLabel={props.values.id ? 'Update Company' : 'Create Company'}
        isDisabled={props.submitting || !props.isDirty()}
        isSubmitting={props.submitting}
        onReset={props.onReset}
      />
    </form>
  );
};

CompanyForm.propTypes = {
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  submitting: PropTypes.bool,
  disableDistributor: PropTypes.bool,
  isDirty: PropTypes.func,
};

CompanyForm.defaultProps = {
  values: {},
  errors: {},
  submitting: false,
  disableDistributor: false,
  isDirty: () => false,
  onSubmit: () => alert('submitted'),
  onReset: () => false,
};

export default CompanyForm;
